import { PropertyLeadsQuery } from 'types/PropertyLeadsQuery';
import { urgencyOptions } from './urgencySelector';

export type FilterType =
    | 'minBudget'
    | 'minConstructionArea'
    | 'propertyType'
    | 'minBedrooms'
    | 'urgency'
    | 'minCommission'
    | 'maxCommission'
    | 'minPlotArea'
    | 'minBathrooms'
    | 'definedArea'
    | 'buyerProfile'
    | 'propertyNeighborhood';

export interface FilterItem {
    text: string;
    type: FilterType;
}

export const formatFilterStrings = (propertyData: Partial<PropertyLeadsQuery>): FilterItem[] => {
    const filterConfigs: Array<{
        condition: (data: Partial<PropertyLeadsQuery>) => boolean;
        format: (data: Partial<PropertyLeadsQuery>) => string;
        type: FilterType;
    }> = [
        {
            condition: (data) => Boolean(data.definedArea),
            format: (data) => `Area: ${data.definedArea}`,
            type: 'definedArea',
        },
        {
            condition: (data) => Boolean(data.propertyNeighborhood?.length),
            format: (data) => `Neighborhoods: ${data.propertyNeighborhood?.join(', ')}`,
            type: 'propertyNeighborhood',
        },
        {
            condition: (data) => (data.minBudget || 0) > 0 || (data.maxBudget || 0) > 0,
            format: (data) => `€${new Intl.NumberFormat().format(data.minBudget! || 0)}-€${new Intl.NumberFormat().format(data.maxBudget! || 0)}`,
            type: 'minBudget',
        },
        {
            condition: (data) => (data.minConstructionArea || 0) > 0 || (data.maxConstructionArea || 0) > 0,
            format: (data) => `${data.minConstructionArea || 0}m²-${data.maxConstructionArea || 0}m²`,
            type: 'minConstructionArea',
        },
        {
            condition: (data) => (data.minPlotArea || 0) > 0 || (data.maxPlotArea || 0) > 0,
            format: (data) => `${data.minPlotArea || 0}m²-${data.maxPlotArea || 0}m²`,
            type: 'minPlotArea',
        },
        {
            condition: (data) => Boolean(data.propertyType),
            format: (data) => data.propertyType!,
            type: 'propertyType',
        },
        {
            condition: (data) => (data.minBedrooms || 0) > 0 || (data.maxBedrooms || 0) > 0,
            format: (data) => `${data.minBedrooms || 0}-${data.maxBedrooms || 0} Bedrooms`,
            type: 'minBedrooms',
        },
        {
            condition: (data) => (data.minBathrooms || 0) > 0 || (data.maxBathrooms || 0) > 0,
            format: (data) => `${data.minBathrooms || 0}-${data.maxBathrooms || 0} Bathrooms`,
            type: 'minBathrooms',
        },
        {
            condition: (data) => Boolean(data.urgency),
            format: (data) => data.urgency!,
            type: 'urgency',
        },
        {
            condition: (data) => (data.minCommission || 0) > 0 || (data.maxCommission || 0) > 0,
            format: (data) => `${data.minCommission || 0}%-${data.maxCommission || 0}%`,
            type: 'minCommission',
        },
        {
            condition: (data) => Boolean(data.buyerProfile),
            format: (data) => data.buyerProfile!,
            type: 'buyerProfile',
        },
    ];

    return filterConfigs
        .filter((config) => config.condition(propertyData))
        .map((config) => ({
            text: config.format(propertyData),
            type: config.type,
        }));
};

export const clearFilter = (
    localFilters: Partial<PropertyLeadsQuery>,
    filterType: FilterType,
    updateLocalFilters: (updates: Partial<PropertyLeadsQuery>) => void,
    otherFilters?: Partial<PropertyLeadsQuery>,
    setOtherFilters?: (updates: Partial<PropertyLeadsQuery>) => void,
) => {
    if (filterType.includes('min')) {
        const correspondingMaxFilter = `max${filterType.slice(3)}` as keyof typeof localFilters;
        delete localFilters[filterType];
        delete localFilters[correspondingMaxFilter];

        otherFilters && delete otherFilters[filterType];
        otherFilters && delete otherFilters[correspondingMaxFilter];
    } else if (filterType === 'definedArea') {
        // When clearing definedArea, also reset usePredefinedLocations state
        delete localFilters[filterType];
        otherFilters && delete otherFilters[filterType];
    } else if (filterType === 'propertyNeighborhood') {
        // When clearing neighborhoods, set to empty array
        localFilters[filterType] = [];
        otherFilters && (otherFilters[filterType] = []);
    } else {
        delete localFilters[filterType];
        otherFilters && delete otherFilters[filterType];
    }
    updateLocalFilters(localFilters);
    setOtherFilters && setOtherFilters?.(otherFilters!);
};
